import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import {
  BrowserAuthError,
  EventType,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";

import { Home } from "./pages/Home/Home";
import * as actions from "./actions";
import { setTokenInterval } from "./utils/AuthTokenFetch";
import {
  setAccessToken,
  getAccessToken,
  removeAccessToken,
} from "./utils/AuthUtils";
import { setIsOnline } from "./utils/OnlineUtils";
import { storeToken } from "./utils/StoreToken";
import { config } from "./config";
import axiosInstance from "./interceptors/axiosInstance";
import { MessageContext } from "./context/MessageContext";
import ToastList from "./components/General/ErrorHandling/Toastlist/ToastList";
import "./App.css";

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes – High risk application

function App() {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [token, setToken] = useState(getAccessToken());
  const [tokenExpiration, setTokenExpiration] = useState(null);
  const [messages, setMessages] = useState([]);

  const ErrorToast = (msg, options = { duration: 10000 }) => {
    const obj = {
      id: Date.now(),
      message: msg,
      type: "failure",
    };
    setMessages((prev) => [...prev, obj]);
    setTimeout(() => {
      removeToast(obj.id);
    }, options.duration);
  };

  const removeToast = (id) => {
    setMessages((prev) => prev.filter((item) => item.id !== id));
  };

  // Handle token storage
  useEffect(() => {
    instance.enableAccountStorageEvents();
    const msalEvent = instance.addEventCallback(async (event) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
        event.eventType === EventType.HANDLE_REDIRECT_END
      ) {
        if (event.payload && event.payload.idToken) {
          if (event.eventType === EventType.LOGIN_SUCCESS) {
            localStorage.setItem("lastLoggedIn", new Date().getTime());
          }
          setAccessToken(event.payload.idToken);
          const result = await storeToken(event.payload.idToken);
          if (
            result ==
              "Failed to authenticate. User not registered with PPA. Contact EY admin." ||
            result == "User is Inactive. Please contact admin!"
          ) {
            ErrorToast(result);
            const delay = await new Promise((resolve, reject) => {
              setTimeout(() => {
                resolve(true);
              }, 5000);
            });
            instance.logoutRedirect();
          }
          setToken(event.payload.idToken);
          setTokenInterval(instance);
          instance.setActiveAccount(event.payload.account);
        }
      } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
        localStorage.clear();
        removeAccessToken();
        setToken("");
        instance.setActiveAccount(null);
      } else if (event.eventType === EventType.ACCOUNT_REMOVED) {
        instance.logoutRedirect();
      }
      return () => {
        instance.removeEventCallback(msalEvent);
      };
    });
  }, []);

  // Handle user inactivity
  useEffect(() => {
    let inactivityTimer;
    const resetInactivityTimer = () => {
      clearTimeout(inactivityTimer);
      startInactivityTimer();
    };
    const startInactivityTimer = () => {
      inactivityTimer = setTimeout(() => {
        setTokenExpiration(new Date().getTime());
      }, INACTIVITY_TIMEOUT_MS);
    };

    const setOnline = () => {
      setIsOnline(true);
    };
    const setOffline = async () => {
      setIsOnline(false);
      ErrorToast("No internet connection!.");
      const delay = await new Promise((resolve) => {
        setTimeout(() => {
          resolve(true);
        }, 5000);
      });
      window.location.reload();
    };

    const activityEvents = [
      "mousemove",
      "mousedown",
      "keydown",
      "keypress",
      "scroll",
      "click",
      "touchstart",
      "touchmove",
    ];

    window.addEventListener("online", setOnline);
    window.addEventListener("offline", setOffline);
    activityEvents.forEach((event) =>
      document.addEventListener(event, resetInactivityTimer),
    );
    startInactivityTimer();

    return () => {
      activityEvents.forEach((event) =>
        document.removeEventListener(event, resetInactivityTimer),
      );
      clearTimeout(inactivityTimer);
      window.removeEventListener("online", setOnline);
      window.removeEventListener("offline", setOffline);
    };
  }, [INACTIVITY_TIMEOUT_MS]);

  // Logut user, if inactive time crossed 30 min
  useEffect(async () => {
    if (tokenExpiration && tokenExpiration < new Date().getTime()) {
      ErrorToast("User Sessioned Out Due to Inactivity");
      const delay = await new Promise((resolve) => {
        setTimeout(() => {
          resolve(true);
        }, 5000);
      });
      try {
        await axiosInstance.post(`${config.BASE_URL}/api/token/logout`);
      } catch (_) {
        // proceed with logout even if server invalidation fails
      } finally {
        removeAccessToken();
        sessionStorage.setItem("inactivity_logout", "true");
        instance.logoutRedirect();
      }
    }
  }, [tokenExpiration]);

  const result = async () => {
    await instance.initialize();
    if (!isAuthenticated || token == "") {
      let tokenResponse = null;

      try {
        tokenResponse = await instance.acquireTokenSilent({
          scopes: ["user.read", "openid"],
        });
      } catch (error) {
        if (
          error instanceof InteractionRequiredAuthError ||
          error instanceof BrowserAuthError
        ) {
          try {
            const redirectResponse = await instance.handleRedirectPromise();
            if (!redirectResponse && inProgress !== "logout") {
              const wasInactivityLogout = sessionStorage.getItem("inactivity_logout") === "true";
              sessionStorage.removeItem("inactivity_logout");
              instance.loginRedirect({
                scopes: ["user.read", "openid"],
                ...(wasInactivityLogout && { prompt: "login" }),
              });
            }
          } catch (err) {
            console.log("Auth Error 2", err);
          }
        }
      }
    }
  };

  useEffect(() => {
    result();
  }, [isAuthenticated]);

  return (
    <>
      <MessageContext.Provider value={{ messages, ErrorToast }}>
        <div className="main__layout">
          <div className="main__container">
            {messages.length > 0 && (
              <ToastList data={messages} removeToast={removeToast}></ToastList>
            )}
            {isAuthenticated && token && <Home />}
          </div>
        </div>
      </MessageContext.Provider>
    </>
  );
}

export default connect(null, actions)(App);
